import { default as React, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Alert from '../../../core/components/Alert.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import countFieldErrors from '../../../core/utils/countFieldErrors.ts';
import useOutputMapperConfigMissing from '../../../customer/components/OutputMapper/useOutputMapperConfigMissing.ts';
import { LIST_OF_SERVICES_OUTPUT_MAPPER_KEY } from '../constants.ts';

const FORM_DEFAULT_VALUES = {
    useOutputMapping: false,
    enabled: false,
    serializerModule: '',
};

const ListOfServicesXmlSerializerConfig = ({ config, useOutputMapping, channelId, onActivate, onSubmit }) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        form.reset({
            useOutputMapping: !!useOutputMapping,
            enabled: config?.enabled,
            serializerModule: config?.serializerModule || '',
        });
    }, [config, useOutputMapping]);

    const outputMapperActive = form.watch('useOutputMapping');

    const outputMapperConfigMissing = useOutputMapperConfigMissing({
        outputMapperKey: LIST_OF_SERVICES_OUTPUT_MAPPER_KEY,
        ownerId: channelId,
    });

    const handleSubmit = async ({ useOutputMapping: useOutputMappingValue, ...formData }) => {
        try {
            if (!config) await onActivate();
            await onSubmit(formData, { useOutputMapping: useOutputMappingValue });
            publishToast({ description: t('configPage.xmlSerializer.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.xmlSerializer.updateError'), status: 'error' });
        }
    };

    return (
        <SettingsFormCard onSubmit={form.handleSubmit(handleSubmit)} title={t('configPage.xmlSerializer.title')}>
            <Controller
                name="useOutputMapping"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.xmlSerializer.form.useOutputMapping')}
                        error={formErrors.useOutputMapping}
                    >
                        <BooleanField {...field} value={field.value ? 'true' : ''} />
                    </FormField>
                )}
            />

            {outputMapperActive && (
                <p className="text-sm text-content-medium">
                    {t('configPage.xmlSerializer.form.useOutputMappingActiveHint')}
                </p>
            )}

            {outputMapperActive && outputMapperConfigMissing && (
                <Alert severity="warning">
                    {t('configPage.xmlSerializer.form.useOutputMappingMissingConfigWarning')}
                </Alert>
            )}

            {!outputMapperActive && (
                <Controller
                    name="enabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('configPage.xmlSerializer.form.enabled')} error={formErrors.enabled}>
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}

            {!outputMapperActive && (
                <Controller
                    name="serializerModule"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.xmlSerializer.form.serializerModule')}
                            error={formErrors.serializerModule}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />
            )}

            <SaveStatus
                isDirty={form.formState.isDirty}
                isSubmitting={form.formState.isSubmitting}
                errorCount={countFieldErrors(form.formState.errors)}
            />
        </SettingsFormCard>
    );
};
export default ListOfServicesXmlSerializerConfig;
